import { uiSimpleBox } from 'components/BionicUI/CutCorners';
import BPIcon, { icons } from 'components/BionicUI/icons/BPIcon';
import BPIconButton from 'components/BionicUI/icons/BPIconButton';
import { useAuth } from 'context/auth.context';
import { cn } from 'lib/utils';
import { FC, ReactNode, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes';
import Notification from './Notification';
import { useClickOutside } from '@react-hooks-library/core';
import { useNotifications } from './useNotifications';

const ProfileMenu: FC = () => {
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const { logOut } = useAuth();
  const navigate = useNavigate();

  const { notifications } = useNotifications();

  const menu = useRef<HTMLDivElement>(null);
  useClickOutside(menu, () => setShowMenu(false));

  const onClickMyProfile = () => navigate(ROUTES.PROFILE);
  const onClickLogOut = () => {
    try {
      logOut();
      navigate(ROUTES.LOGIN);
    } catch (e) {
      console.error(e);
    }
  };

  // NB. when wiring up notifications, don't forget to also update BurgerMenu.tsx foir mobile sizes.

  return (
    <>
      <div className="hidden md:block relative animate-fade-in">
        <BPIconButton
          icon="profile"
          disabled={showMenu}
          iconClassName="fill-white"
          onClick={() => setShowMenu(true)}
        />
        {notifications?.length > 0 && (
          <div
            className={cn(
              'grid absolute -top-[2px] -right-[3px] rounded-full bg-bionic-error min-w-[18px] min-h-[18px] p-[1px] justify-center items-center font-semiBold text-[10px]',
              notifications.length < 10 ? 'aspect-square' : ''
            )}
          >
            {notifications.length}
          </div>
        )}
      </div>
      {showMenu && (
        <div
          ref={menu}
          className={cn(
            'block absolute top-[90px] right-0 page-right-padding',
            notifications?.length > 0 ? 'w-[560px] ' : 'w-auto'
          )}
        >
          <div className="grid relative grid-cols-[1fr_auto] w-full bg-bionic-greyDark rounded-[8px] overflow-hidden">
            <Border />
            {notifications?.length > 0 && (
              <div className="block relative px-[24px] py-[20px]">
                <div className="grid relative gap-2">
                  {notifications.map((notification, idx) => (
                    <Notification
                      key={`notification_${idx}`}
                      idx={idx}
                      notification={notification}
                    />
                  ))}
                </div>
              </div>
            )}
            <div className="grid relative grid-rows-[auto_1fr] gap-5 pl-[20px] pr-[24px] py-[20px] text-[14px] font-semiBold items-start">
              {notifications?.length > 0 && <Divider />}
              <MenuButton onClick={onClickMyProfile} icon="profile">
                My Profile
              </MenuButton>
              <MenuButton onClick={onClickLogOut} icon="logout" red>
                Log Out
              </MenuButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Divider = () => (
  <div className="block absolute top-[1px] left-0 w-[1px] h-[calc(100%-2px)] bg-gradient-to-b from-white/30 to-white/10" />
);

interface MenuButtonProps {
  children: ReactNode;
  onClick: () => void;
  icon: keyof typeof icons;
  red?: boolean;
}

const MenuButton: FC<MenuButtonProps> = ({
  children,
  onClick,
  icon,
  red = false,
}) => (
  <button
    onClick={onClick}
    className="grid relative grid-cols-[auto_1fr] gap-2 items-center w-full pr-[40px] hover:opacity-50 text-left"
  >
    <BPIcon
      icon={icon}
      className={cn(red ? 'fill-bionic-red' : 'fill-white')}
    />
    <div className={cn(red ? 'text-bionic-red' : 'text-white')}>{children}</div>
  </button>
);

const Border = () => (
  <div
    className={cn(
      'block absolute top-0 left-0 w-full h-full bg-gradient-to-b from-white/30 to-white/10',
      uiSimpleBox.border
    )}
  />
);

export default ProfileMenu;
